import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import isCollaboratable from 'auth/isCollaboratable';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {CompactCollaborationManagementSection} from 'collaborator-components/CollaborationManagementSection';
import {INVENTORY_SECTION, NOTEBOOK_SECTION} from 'constants/FileBrowserConstants';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants, {NOTEBOOK_ITEMS} from 'constants/ModelTypeConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  BioentityMetadataQuery,
  BioentityMetadataQueryVariables,
  BioentityMetadataQuery_bioentity_BasicFolderItemNode_sampleDefinitionLinks_SampleDefinitionLinkNode as SampleDefinitionLinkNode,
} from 'metadata/__generated/BioentityMetadataQuery';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import {isTrialSampleInterfaceEnabled} from 'registry/trial-interfaces/isTrialSampleInterfaceEnabled';
import EntityStore from 'stores/EntityStore';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {isItemAutoLinkable, isItemStudyable} from 'studies/studyable/StudyableUtils';
import EditableDisplayId from 'studies/workspace/EditableDisplayId';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import ParentLink from 'ui-library/ParentLink';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';
import {Optional} from 'util/Types';

import {RegistrationOriginGraphQL} from '../../graphql-fragments/registry/__generated/RegistrationOriginGraphQL';
import AuthorList from './AuthorList';
import RegistryIdList from './RegistryIdList';

export function SectionWrapper(props: {
  children: React.ReactNode;
  headerName?: string;
  elementName?: string;
}): JSX.Element {
  return (
    <div
      className="metadata-section"
      {...getTestProps({component: 'SectionWrapper', element: props.elementName})}
    >
      {props.headerName && <div className="metadata-header">{props.headerName}</div>}
      {props.children}
    </div>
  );
}

export const BIOENTITY_METADATA_QUERY = gql`
  query BioentityMetadataQuery($id: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    bioentity(id: $id) {
      ... on RegistryItemInterface {
        id
        registrationOrigin {
          ...RegistrationOriginGraphQL
        }
      }
      ... on TrialSampleItemInterface {
        sampleDefinitionLinks {
          sampleDefinition {
            ...InaccessibleInfoGraphQL
            ... on SampleDefinitionNode {
              id
              label
            }
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('RegistrationOrigin').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface MetadataEditorInfoSectionProps {
  fileId: string;
  authors?: Array<User> | null;
  projectFolder: NumberIDFolder | StringIDFolder | null;
  hasFileSchemaSupport: boolean;
  folder?: NumberIDFolder | StringIDFolder;
  hasRegistrySupport: boolean;
  permissions: Permissions;
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  humanId?: string;
  createdAt?: string;
  finalized?: {
    isFinalized: boolean;
    finalizedAt: Optional<string>;
  };
  registryId?: string | null;
  isNonInteractiveProcess?: boolean;
  shouldAllowTeamAuthorsLookup?: boolean;
}

class MetadataEditorInfoSection extends React.Component<
  MetadataEditorInfoSectionProps & {
    registrationOrigin: RegistrationOriginGraphQL | null;
    sampleDefinitionLinks: Array<SampleDefinitionLinkNode> | null;
    isLoadingBioentityMetadata?: boolean;
  }
> {
  private isRegistered = (): boolean => Boolean(this.props.registryId);

  private showContainingFolder(folder: NumberIDFolder | StringIDFolder): void {
    const section = NOTEBOOK_ITEMS.includes(APIUtil.apiIdToType(this.props.fileId))
      ? NOTEBOOK_SECTION
      : INVENTORY_SECTION;
    listFolderSection(folder, section);
  }

  private renderAuthors(): JSX.Element {
    const isRegistered = this.isRegistered();
    return (
      <SectionWrapper headerName="Authors" elementName="authorsSection">
        <AuthorList
          authors={this.props.authors}
          fileId={this.props.fileId}
          isReadOnly={
            this.props.isNonInteractiveProcess ||
            !isFeatureEnabled({
              feature: StandardFeature.EDIT,
              item: {id: this.props.fileId, permissions: this.props.permissions},
              itemState: {isRegistered},
            }) ||
            APIUtil.isBulkAssembly(this.props.fileId)
          }
          isRegistered={isRegistered}
          currentUser={EntityStore.currentUser()}
          shouldAllowTeams={this.props.shouldAllowTeamAuthorsLookup ?? false}
        />
      </SectionWrapper>
    );
  }

  private renderFolderSection(): JSX.Element | null {
    const folderToDisplay = this.props.projectFolder ?? this.props.folder;
    if (folderToDisplay == null) {
      return null;
    }
    return (
      <SectionWrapper headerName={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} elementName="folderSection">
        <ParentLink
          icon={ModelTypeConstants.FOLDER}
          onClickParent={() => {
            this.showContainingFolder(folderToDisplay);
          }}
          parentName={folderToDisplay.name}
          tooltipTitle="Show containing project folder"
        />
      </SectionWrapper>
    );
  }

  private renderRegistryIdsSection(): JSX.Element {
    return (
      <div
        className="metadata-section"
        {...getTestProps({component: 'SectionWrapper', element: 'registryIdsSection'})}
      >
        <RegistryIdList
          fileId={this.props.fileId}
          isRegistryWritable={this.props.permissions.registryIdsChangeable ?? false}
          registryIds={this.props.bioentityAliasInfos.filter(
            (bioentityAliasInfo) => bioentityAliasInfo.registryCollectionOwner != null
          )}
        />
      </div>
    );
  }

  private renderRegisteredInSection(): JSX.Element {
    // This section is only rendered if there's an origin entry, or it's still loading
    if (this.props.isLoadingBioentityMetadata) {
      return <HorizontalThrobber />;
    } else {
      return (
        <SectionWrapper headerName="Registered in" elementName="registeredInSection">
          <BenchChip
            size={ChipSize.SMALL}
            id={this.props.registrationOrigin!.originEntry!.id}
            isLink={true}
          />
        </SectionWrapper>
      );
    }
  }

  private renderHumanIdSection(): JSX.Element {
    return (
      <SectionWrapper headerName="ID" elementName="humanIdSection">
        {APIUtil.isStudy(this.props.fileId) ? (
          <EditableDisplayId studyId={this.props.fileId} />
        ) : (
          this.props.humanId
        )}
      </SectionWrapper>
    );
  }

  private renderCreatedAt(): JSX.Element {
    return (
      <SectionWrapper headerName="Created" elementName="createdAtSection">
        {readableTime(this.props.createdAt)}
      </SectionWrapper>
    );
  }

  private renderFinalizedAt(): JSX.Element {
    const {finalizedAt} = this.props.finalized!;
    return (
      <SectionWrapper headerName="Finalized" elementName="finalizedAtSection">
        <span>{finalizedAt != null ? readableTime(finalizedAt) : 'Unknown'}</span>
      </SectionWrapper>
    );
  }

  private renderCollaborationManagementSection(): JSX.Element {
    return (
      <SectionWrapper headerName="Collaborators" elementName="collaboratorsSection">
        <CompactCollaborationManagementSection policeableId={this.props.fileId} />
      </SectionWrapper>
    );
  }

  private maybeRenderSampleDefinitionField(): JSX.Element | null {
    if (this.props.isLoadingBioentityMetadata) {
      return <HorizontalThrobber />;
    }
    if (!BenchlingConfig.REGISTRY_ENABLE_TRIAL_SAMPLES || this.props.sampleDefinitionLinks == null) {
      return null;
    }

    const sampleDefinitionLabels = this.props.sampleDefinitionLinks
      .map(({sampleDefinition}) =>
        sampleDefinition != null
          ? !isInaccessible(sampleDefinition)
            ? sampleDefinition.label
            : null
          : 'None'
      )
      .filter((label) => label != null)
      .join(', ');
    const useFadedDisplay = sampleDefinitionLabels === 'None';

    if (sampleDefinitionLabels.length > 0) {
      return (
        <Col sm={6}>
          <SectionWrapper headerName="Sample definition" elementName="sampleDefinitionSection">
            <div className={useFadedDisplay ? 'u-faded' : undefined}>{sampleDefinitionLabels}</div>
          </SectionWrapper>
        </Col>
      );
    }
    return null;
  }

  override render(): JSX.Element {
    const coreObject = {id: this.props.fileId};
    const columns = _.compact([
      this.props.authors != null && <Col sm={6}>{this.renderAuthors()}</Col>,
      <Col sm={6}>
        {(() => {
          if (this.props.projectFolder != null || this.props.hasFileSchemaSupport) {
            return this.renderFolderSection();
          }
          return null;
        })()}
      </Col>,
      BenchlingConfig.STUDIES_ENABLE && isItemStudyable(coreObject) && (
        <Col sm={6}>
          <SectionWrapper headerName="Studies" elementName="studiesSection">
            <EditableStudiesList
              studyable={coreObject}
              isWritable={this.props.permissions.writable}
              isAutoLinkable={isItemAutoLinkable(coreObject)}
              maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
            />
          </SectionWrapper>
        </Col>
      ),
      this.maybeRenderSampleDefinitionField(),
      this.props.hasRegistrySupport && this.isRegistered() && (
        <Col sm={6}>{this.renderRegistryIdsSection()}</Col>
      ),
      this.props.hasRegistrySupport &&
        this.isRegistered() &&
        (this.props.registrationOrigin?.originEntry || this.props.isLoadingBioentityMetadata) && (
          <Col sm={6}>{this.renderRegisteredInSection()}</Col>
        ),
      this.props.humanId != null && <Col sm={6}>{this.renderHumanIdSection()}</Col>,
      this.props.createdAt != null && <Col sm={6}>{this.renderCreatedAt()}</Col>,
      this.props.finalized?.isFinalized != null && <Col sm={6}>{this.renderFinalizedAt()}</Col>,
      isCollaboratable({id: this.props.fileId}) && (
        <Col sm={6}>{this.renderCollaborationManagementSection()}</Col>
      ),
    ]);

    const result = [];
    for (let i = 0, end = columns.length; i < end; i += 2) {
      result.push(
        <Row bufferRowReset={true} key={i}>
          {columns[i]}
          {columns[i + 1]}
        </Row>
      );
    }

    return <div>{result}</div>;
  }
}

function MetadataEditorInfoSectionWithRegistrySupport(
  props: MetadataEditorInfoSectionProps
): JSX.Element | null {
  const result = useBenchlingQuery<BioentityMetadataQuery, BioentityMetadataQueryVariables>(
    BIOENTITY_METADATA_QUERY,
    {
      variables: {id: props.fileId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return (
      <MetadataEditorInfoSection
        {...props}
        registrationOrigin={null}
        sampleDefinitionLinks={null}
        isLoadingBioentityMetadata={true}
      />
    );
  } else if (result.type === QueryResultType.SUCCESS) {
    assert(
      Boolean(
        !isInaccessible(result.data.bioentity) || props.fileId === window.bootstrap?.sharedFolderItemAPIId
      ),
      'Expected accessible bioentity'
    );
    let sampleDefinitionLinks = null;
    if (
      isTrialSampleInterfaceEnabled(result.data.bioentity) &&
      'sampleDefinitionLinks' in result.data.bioentity
    ) {
      sampleDefinitionLinks = result.data.bioentity.sampleDefinitionLinks;
    }
    return (
      <MetadataEditorInfoSection
        {...props}
        registrationOrigin={
          isInaccessible(result.data.bioentity) ? null : result.data.bioentity.registrationOrigin
        }
        sampleDefinitionLinks={sampleDefinitionLinks}
        isLoadingBioentityMetadata={false}
      />
    );
  } else {
    return null;
  }
}

export default function QueryingMetadataEditorInfoSection(
  props: MetadataEditorInfoSectionProps
): JSX.Element | null {
  return props.hasRegistrySupport ? (
    <MetadataEditorInfoSectionWithRegistrySupport {...props} />
  ) : (
    <MetadataEditorInfoSection {...props} registrationOrigin={null} sampleDefinitionLinks={null} />
  );
}
